import { HttpParams } from '@angular/common/http'
import { DecimalPipe } from '@angular/common'
import { Component, EventEmitter, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'

import { BaseComponent } from '../../shared'

export interface PcoDashboardDrillthrough {
    ids?: string[]
    openPcoId?: string
    label?: string
}

interface PcoDashboardKpi {
    openCount: number
    openApproxCost: number
    hotCount: number
    awaitingMyActionCount: number
    awaitingMyActionIds: string[]
    awaitingInternalApprovalCount: number
    awaitingInternalApprovalCost: number
    awaitingInternalApprovalIds: string[]
    awaitingOwnerCount: number
    awaitingOwnerCost: number
    awaitingOwnerAverageDays: number
    awaitingOwnerIds: string[]
    stalledCount: number
    stalledIds: string[]
    executedThisMonth: number
    importedThisMonth: number
    averageCycleDaysLast90: number
    cycleTimeSampleCount: number
}

interface PcoStageBucket {
    stage: string
    count: number
    approxCostTotal: number
    stalledCount: number
    recordIds: string[]
}

interface PcoDashboardRecord {
    id: string
    job: string
    jobDisplay: string
    pcoNumber: string
    description: string
    approxCost: number | null
    daysInStage: number
    isHot: boolean
}

interface PcoResponsibleGroup {
    key: string
    responsibleName: string
    responsibleRole: string
    stage: string
    count: number
    approxCostTotal: number
    oldestDays: number
    recordIds: string[]
    records: PcoDashboardRecord[]
}

interface PcoAgingRow {
    id: string
    job: string
    jobDisplay: string
    pcoNumber: string
    description: string
    stage: string
    responsibleName: string
    daysInStage: number
    approxCost: number | null
    isHot: boolean
}

interface PcoJobRollup {
    job: string
    jobDisplay: string
    pmName: string
    jcaName: string
    openCount: number
    openApproxCost: number
    hotCount: number
    awaitingOwnerCount: number
    oldestDays: number
    recordIds: string[]
}

interface PcoStageDuration {
    stage: string
    averageDays: number
    medianDays: number
    sampleCount: number
}

interface PcoPeriodCount {
    label: string
    initiated: number
    ownerExecuted: number
    importedIntoVista: number
}

interface PcoCompositionSlice {
    label: string
    count: number
    approxCostTotal: number
}

interface PcoComposition {
    byWorkType: PcoCompositionSlice[]
    byPricingMethod: PcoCompositionSlice[]
    byInitiatedBy: PcoCompositionSlice[]
}

interface PcoRiskFlag {
    key: string
    label: string
    count: number
    approxCostTotal: number
    recordIds: string[]
}

interface PcoDashboardJobOption {
    job: string
    display: string
}

interface PcoDashboardMetrics {
    kpi: PcoDashboardKpi
    pipeline: PcoStageBucket[]
    actionQueue: PcoResponsibleGroup[]
    stalled: PcoAgingRow[]
    byJob: PcoJobRollup[]
    stageDurations: PcoStageDuration[]
    monthlyTrend: PcoPeriodCount[]
    composition: PcoComposition
    riskFlags: PcoRiskFlag[]
    filterJobs: PcoDashboardJobOption[]
}

const emptyKpi = (): PcoDashboardKpi => ({
    openCount: 0,
    openApproxCost: 0,
    hotCount: 0,
    awaitingMyActionCount: 0,
    awaitingMyActionIds: [],
    awaitingInternalApprovalCount: 0,
    awaitingInternalApprovalCost: 0,
    awaitingInternalApprovalIds: [],
    awaitingOwnerCount: 0,
    awaitingOwnerCost: 0,
    awaitingOwnerAverageDays: 0,
    awaitingOwnerIds: [],
    stalledCount: 0,
    stalledIds: [],
    executedThisMonth: 0,
    importedThisMonth: 0,
    averageCycleDaysLast90: 0,
    cycleTimeSampleCount: 0,
})

const emptyMetrics = (): PcoDashboardMetrics => ({
    kpi: emptyKpi(),
    pipeline: [],
    actionQueue: [],
    stalled: [],
    byJob: [],
    stageDurations: [],
    monthlyTrend: [],
    composition: { byWorkType: [], byPricingMethod: [], byInitiatedBy: [] },
    riskFlags: [],
    filterJobs: [],
})

@Component({
    selector: 'app-pending-change-orders-dashboard',
    templateUrl: './pending-change-orders-dashboard.component.html',
    styleUrls: ['./pending-change-orders-dashboard.component.css'],
    standalone: true,
    imports: [DecimalPipe, FormsModule],
})
export class PendingChangeOrdersDashboardComponent extends BaseComponent {
    @Output() viewRecords = new EventEmitter<PcoDashboardDrillthrough>()

    metrics: PcoDashboardMetrics = emptyMetrics()
    metricsLoadError = false
    selectedJob = ''
    hotOnly = false
    expandedGroups = new Set<string>()

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        let params = new HttpParams()
        if (this.selectedJob) {
            params = params.set('job', this.selectedJob)
        }
        if (this.hotOnly) {
            params = params.set('hotOnly', 'true')
        }

        this.cancelableRequest(
            'pcoDashboard',
            true,
            this.services.data.get<PcoDashboardMetrics>('/api/PendingChangeOrder/GetDashboardMetrics', params).subscribe({
                next: result => {
                    this.metrics = result ?? emptyMetrics()
                    this.metrics.kpi = this.metrics.kpi ?? emptyKpi()
                    this.metrics.pipeline = this.metrics.pipeline ?? []
                    this.metrics.actionQueue = this.metrics.actionQueue ?? []
                    this.metrics.stalled = this.metrics.stalled ?? []
                    this.metrics.byJob = this.metrics.byJob ?? []
                    this.metrics.stageDurations = this.metrics.stageDurations ?? []
                    this.metrics.monthlyTrend = this.metrics.monthlyTrend ?? []
                    this.metrics.composition = this.metrics.composition ?? {
                        byWorkType: [],
                        byPricingMethod: [],
                        byInitiatedBy: [],
                    }
                    this.metrics.riskFlags = this.metrics.riskFlags ?? []
                    this.metrics.filterJobs = this.metrics.filterJobs ?? []
                    this.metricsLoadError = false
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.metrics = emptyMetrics()
                    this.metricsLoadError = true
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    onFilterChange() {
        this.refresh()
    }

    drill(ids: string[] | undefined, label: string) {
        if (!ids?.length) return
        this.viewRecords.emit({ ids, label })
    }

    openRecord(id: string) {
        this.viewRecords.emit({ openPcoId: id })
    }

    toggleGroup(key: string) {
        if (this.expandedGroups.has(key)) {
            this.expandedGroups.delete(key)
        } else {
            this.expandedGroups.add(key)
        }
    }

    isExpanded(key: string) {
        return this.expandedGroups.has(key)
    }

    formatUsd(value: number | null | undefined): string {
        return (value ?? 0).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        })
    }

    maxPipelineCount(): number {
        return Math.max(...this.metrics.pipeline.map(p => p.count), 1)
    }

    maxTrendCount(): number {
        return Math.max(
            ...this.metrics.monthlyTrend.flatMap(m => [m.initiated, m.ownerExecuted, m.importedIntoVista]),
            1,
        )
    }

    openRecordIds(): string[] {
        return this.metrics.pipeline.flatMap(p => p.recordIds ?? [])
    }

    durationBarWidth(days: number): number {
        const max = Math.max(...this.metrics.stageDurations.map(s => s.averageDays), 1)
        return Math.max((days / max) * 100, days > 0 ? 4 : 0)
    }
}
